import { LoadingState } from "@repo/app-types/client/client";
import type { AdminApiEntitlementUsage } from "@repo/app-types/entities/entitlement";
import { BillingPlansEnumToDisplayName } from "@repo/app-types/helpers/billingFeatureGating";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/index";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconPencil } from "@tabler/icons-react";
import { format } from "date-fns";
import { type FC, useEffect, useMemo, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useCompanyEntitlementsQuery } from "../../../api/entities/company.js";
import { PageLayout } from "../../../components/layout/pageLayout.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchBillingInfo,
  selectDefaultPaymentMethod,
} from "../../billing/billingSlice.js";
import { selectCompany } from "../../company/companySlice.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";

export const BillingSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [portalLoadingState, setPortalLoadingState] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const defaultPaymentMethod = useAppSelector(selectDefaultPaymentMethod);
  const {
    data: entitlements,
    isLoading: isLoadingEntitlements,
    isError: isErrorEntitlements,
  } = useCompanyEntitlementsQuery();

  useEffect(() => {
    dispatch(fetchBillingInfo());
  }, [dispatch]);

  const openStripeBillingPortal = async (isPaymentMethodUpdate?: boolean) => {
    setPortalLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const path = `/billing/portal-session${
        isPaymentMethodUpdate ? "?type=payment_method_update" : ""
      }`;
      const { data } = await axios.post(path);
      setPortalLoadingState(LoadingState.LOADED);

      // Navigate to the portal url rather than opening in popup
      // so we don't have listen to webhooks when payment methods are added
      window.location.href = data;
    } catch (err) {
      setPortalLoadingState(LoadingState.ERROR);
    }
  };

  const entitlementColumns: readonly EntityTableColumnDefinition<AdminApiEntitlementUsage>[] =
    useMemo(
      () => [
        {
          id: "starts_at",
          isRowHeader: true,
          header: "Start date",
          className: "w-[25%]",
          renderCell: (row) => format(new Date(row.starts_at), "MMM dd yyyy"),
        },
        {
          id: "expires_at",
          header: "Expiration date",
          className: "w-[25%]",
          renderCell: (row) => format(new Date(row.expires_at), "MMM dd yyyy"),
        },
        {
          id: "max_interviews",
          header: "Max interviews",
          className: "w-[25%]",
          renderCell: (row) => row.max_interviews.toString(),
        },
        {
          id: "used_interviews",
          header: "Used interviews",
          className: "w-[25%]",
          renderCell: (row) => row.used_interviews.toString(),
        },
      ],
      [],
    );

  if (!company) {
    return (
      <PageLayout>
        <PageHeader
          width={PageWidth.Form}
          title={settingsSections.billing.label}
        />
      </PageLayout>
    );
  }

  return (
    <SettingsSectionPage title={settingsSections.billing.label}>
      <div className="border-b border-gray-900/10 py-4">
        <h2 className="pb-2 text-lg leading-6 font-semibold text-foreground">
          Plan
        </h2>
        <div className="flex flex-col space-y-4 py-2 text-sm">
          {company.billing_current_plan && (
            <div>
              <Label size={Size.SMALL}>Plan name</Label>
              <div className="text-foreground">
                {BillingPlansEnumToDisplayName[company.billing_current_plan]}
              </div>
            </div>
          )}
          {defaultPaymentMethod && defaultPaymentMethod.is_card ? (
            <div>
              <Label size={Size.SMALL}>Payment method</Label>
              <div className="group flex flex-row space-x-1 text-foreground">
                <span>
                  {`${capitalize(defaultPaymentMethod.brand)} ending in ${
                    defaultPaymentMethod.last4
                  } (${defaultPaymentMethod.exp_month >= 10 ? "" : "0"}${
                    defaultPaymentMethod.exp_month
                  }/${defaultPaymentMethod.exp_year})`}
                </span>
                <div className="hidden group-hover:flex">
                  <IconButton
                    variant={ButtonVariant.Tertiary}
                    icon={<IconPencil className="h-3 w-3" />}
                    onClick={() => {
                      const isPaymentMethodUpdate = true;
                      openStripeBillingPortal(isPaymentMethodUpdate);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
          {company.billing_company_id && (
            <div className="flex flex-row space-x-3">
              <Button
                onClick={() => openStripeBillingPortal()}
                variant={ButtonVariant.Secondary}
                isLoading={portalLoadingState === LoadingState.LOADING}
              >
                Manage Subscription &amp; Invoices
              </Button>
            </div>
          )}
          {portalLoadingState === LoadingState.ERROR && (
            <div>
              {"Sorry, an error occurred. Please try again or "}
              <Link href="mailto:support@alpharun.com">
                reach out to our team
              </Link>

              {" for help."}
            </div>
          )}
          <div>
            {`Questions about your plan? `}
            <Link href="mailto:support@alpharun.com">Email us</Link>
            {" or "}
            <Link href="https://cal.frontapp.com/alpharun/paul/7fc09a3a">
              book a meeting
            </Link>
            {"."}
          </div>
        </div>
      </div>
      {entitlements && entitlements.length > 0 && (
        <div className="border-t border-gray-900/10 py-4">
          <h2 className="pb-2 text-lg leading-6 font-semibold text-foreground">
            Plan limits
          </h2>
          <div className="py-2">
            <EntityTable
              aria-label="Entitlements"
              columns={entitlementColumns}
              entities={entitlements}
              isLoading={isLoadingEntitlements}
              isError={isErrorEntitlements}
              emptyMessage="No entitlements found."
            />
          </div>
        </div>
      )}
    </SettingsSectionPage>
  );
};
